import Swal from 'sweetalert2';
import {
    CreateS3Folder,
    DeleteS3Object,
    MoveS3Object,
    RenameS3Object,
    UploadS3File,
} from '../../../services/command/aiPrompt/S3BrowserCommand.services';
import { isValidFolderSegment } from './SCUIS3Browser.helpers';
import type { CtxTarget } from './S3BrowserContextMenu';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function resolveMoveKeysForContextTarget(
    target: CtxTarget,
    fileSelectionEnabled: boolean,
    selectedKeys: Set<string>
): string[] {
    if (target.kind === 'folder') {
        return [target.item.key];
    }
    const key = target.item.key;
    if (fileSelectionEnabled && selectedKeys.size > 0 && selectedKeys.has(key)) {
        return Array.from(selectedKeys);
    }
    return [key];
}

export function removeMovedKeysFromSelection(prev: Set<string>, keysToMove: string[]): Set<string> {
    const next = new Set(prev);
    for (const sourceKey of keysToMove) {
        if (sourceKey.endsWith('/')) {
            for (const key of Array.from(next)) {
                if (key.startsWith(sourceKey)) {
                    next.delete(key);
                }
            }
        } else {
            next.delete(sourceKey);
        }
    }
    return next;
}

export async function executeMoveObjects(params: {
    moveKeys: string[];
    moveDestPrefix: string;
    prefix: string;
    t: Translate;
    setMoving: (value: boolean) => void;
    setSelectedKeys: (updater: (prev: Set<string>) => Set<string>) => void;
    resetMoveDialog: () => void;
    reload: (prefix: string) => Promise<void>;
}): Promise<void> {
    if (params.moveKeys.length === 0) return;

    const dest = params.moveDestPrefix.trim();
    if (dest.includes('..')) {
        await Swal.fire({ icon: 'warning', title: params.t('aiPrompt.s3Browser.moveInvalidPath') });
        return;
    }

    const keysToMove = [...params.moveKeys];
    try {
        params.setMoving(true);
        let failed = 0;
        let lastError = '';

        for (const sourceKey of keysToMove) {
            try {
                await MoveS3Object({ sourceKey, destinationPrefix: dest });
            } catch (err: unknown) {
                failed++;
                const data = (err as { data?: { message?: string } })?.data;
                lastError = data?.message ?? (err as Error)?.message ?? '';
            }
        }

        params.setSelectedKeys((prev) => removeMovedKeysFromSelection(prev, keysToMove));
        params.resetMoveDialog();
        await params.reload(params.prefix);

        if (failed > 0) {
            await Swal.fire({
                icon: 'warning',
                title: params.t('aiPrompt.s3Browser.movePartialError', {
                    failed,
                    total: keysToMove.length,
                }),
                text: lastError || undefined,
            });
            return;
        }

        await Swal.fire({
            icon: 'success',
            title:
                keysToMove.length > 1
                    ? params.t('aiPrompt.s3Browser.moveSuccessMany', { count: keysToMove.length })
                    : params.t('aiPrompt.s3Browser.moveSuccess'),
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (err: unknown) {
        const data = (err as { data?: { message?: string } })?.data;
        const msg = data?.message ?? params.t('aiPrompt.s3Browser.moveError');
        await Swal.fire({ icon: 'error', title: msg });
    } finally {
        params.setMoving(false);
    }
}

export async function executeRenameObject(params: {
    renameTarget: CtxTarget | null;
    renameValue: string;
    prefix: string;
    t: Translate;
    setRenaming: (value: boolean) => void;
    resetRenameDialog: () => void;
    reload: (prefix: string) => Promise<void>;
}): Promise<void> {
    if (!params.renameTarget) return;

    const segment = params.renameValue.trim();
    if (params.renameTarget.kind === 'folder') {
        if (!isValidFolderSegment(segment)) {
            await Swal.fire({ icon: 'warning', title: params.t('aiPrompt.s3Browser.invalidFolderName') });
            return;
        }
    } else if (
        !segment ||
        segment.length > 255 ||
        segment.includes('/') ||
        segment.includes('\\') ||
        /[\0\r\n]/.test(segment)
    ) {
        await Swal.fire({ icon: 'warning', title: params.t('aiPrompt.s3Browser.invalidFolderName') });
        return;
    }

    try {
        params.setRenaming(true);
        await RenameS3Object({ sourceKey: params.renameTarget.item.key, newName: segment });
        params.resetRenameDialog();
        await params.reload(params.prefix);
        await Swal.fire({
            icon: 'success',
            title: params.t('aiPrompt.s3Browser.renameSuccess'),
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (err: unknown) {
        const data = (err as { data?: { message?: string } })?.data;
        const msg = data?.message ?? params.t('aiPrompt.s3Browser.renameError');
        await Swal.fire({ icon: 'error', title: msg });
    } finally {
        params.setRenaming(false);
    }
}

export async function executeDeleteObject(params: {
    target: CtxTarget;
    prefix: string;
    t: Translate;
    setLoading: (value: boolean) => void;
    reload: (prefix: string) => Promise<void>;
}): Promise<void> {
    const confirmed = await Swal.fire({
        title: params.t('aiPrompt.s3Browser.deleteConfirmTitle'),
        text: params.t('aiPrompt.s3Browser.deleteConfirmText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: params.t('aiPrompt.s3Browser.ctxDelete'),
        cancelButtonText: params.t('aiPrompt.s3Browser.cancel'),
        confirmButtonColor: '#d33',
    });

    if (!confirmed.isConfirmed) return;

    try {
        params.setLoading(true);
        await DeleteS3Object({ key: params.target.item.key });
        await params.reload(params.prefix);
        await Swal.fire({
            icon: 'success',
            title: params.t('aiPrompt.s3Browser.deleteSuccess'),
            timer: 1800,
            showConfirmButton: false,
        });
    } catch {
        await Swal.fire({ icon: 'error', title: params.t('aiPrompt.s3Browser.deleteError') });
    } finally {
        params.setLoading(false);
    }
}

export async function executeCreateFolder(params: {
    prefix: string;
    newFolderName: string;
    t: Translate;
    setCreatingFolder: (value: boolean) => void;
    resetFolderDialog: () => void;
    reload: (prefix: string) => Promise<void>;
}): Promise<void> {
    if (!isValidFolderSegment(params.newFolderName)) {
        await Swal.fire({
            icon: 'warning',
            title: params.t('aiPrompt.s3Browser.invalidFolderName'),
        });
        return;
    }

    try {
        params.setCreatingFolder(true);
        const trimmedName = params.newFolderName.trim();
        await CreateS3Folder({
            prefix: params.prefix,
            name: trimmedName,
        });
        params.resetFolderDialog();
        const newPrefix = params.prefix.endsWith('/')
            ? `${params.prefix}${trimmedName}/`
            : `${params.prefix}/${trimmedName}/`;
        await params.reload(newPrefix);
        await Swal.fire({
            icon: 'success',
            title: params.t('aiPrompt.s3Browser.createFolderSuccess'),
            timer: 2000,
            showConfirmButton: false,
        });
    } catch (err: unknown) {
        const data = (err as { data?: { message?: string } })?.data;
        const msg = data?.message ?? params.t('aiPrompt.s3Browser.createFolderError');
        await Swal.fire({ icon: 'error', title: msg });
    } finally {
        params.setCreatingFolder(false);
    }
}

export async function executeDeleteSelectedFiles(params: {
    selectedKeys: Set<string>;
    prefix: string;
    t: Translate;
    setLoading: (value: boolean) => void;
    clearSelection: () => void;
    reload: (prefix: string) => Promise<void>;
}): Promise<void> {
    if (params.selectedKeys.size === 0) {
        await Swal.fire({ icon: 'info', title: params.t('aiPrompt.s3Browser.nothingSelected') });
        return;
    }

    const confirmed = await Swal.fire({
        title: params.t('aiPrompt.s3Browser.deleteConfirmTitle'),
        text: params.t('aiPrompt.s3Browser.deleteSelectedConfirmText', { count: params.selectedKeys.size }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: params.t('aiPrompt.s3Browser.ctxDelete'),
        cancelButtonText: params.t('aiPrompt.s3Browser.cancel'),
        confirmButtonColor: '#d33',
    });

    if (!confirmed.isConfirmed) return;

    try {
        params.setLoading(true);
        for (const key of Array.from(params.selectedKeys)) {
            await DeleteS3Object({ key });
        }
        params.clearSelection();
        await params.reload(params.prefix);
        await Swal.fire({
            icon: 'success',
            title: params.t('aiPrompt.s3Browser.deleteSuccess'),
            timer: 1800,
            showConfirmButton: false,
        });
    } catch {
        await Swal.fire({ icon: 'error', title: params.t('aiPrompt.s3Browser.deleteError') });
    } finally {
        params.setLoading(false);
    }
}

export async function executeUploadFiles(params: {
    acceptedFiles: File[];
    prefix: string;
    readOnly?: boolean;
    disableUpload?: boolean;
    customUploadHandler?: (files: File[], prefix: string) => Promise<void>;
    t: Translate;
    setUploading: (value: boolean) => void;
    reload: (prefix: string) => Promise<void>;
    onUploadComplete?: () => void;
}): Promise<void> {
    if (params.readOnly || params.disableUpload || params.acceptedFiles.length === 0) return;

    if (params.customUploadHandler) {
        await params.customUploadHandler(params.acceptedFiles, params.prefix);
        await params.reload(params.prefix);
        return;
    }

    try {
        params.setUploading(true);
        for (const file of params.acceptedFiles) {
            await UploadS3File(params.prefix, file);
        }
        await Swal.fire({
            icon: 'success',
            title: params.t('aiPrompt.s3Browser.uploadSuccess'),
            timer: 1800,
            showConfirmButton: false,
        });
        await params.reload(params.prefix);
        params.onUploadComplete?.();
    } catch {
        await Swal.fire({ icon: 'error', title: params.t('aiPrompt.s3Browser.uploadError') });
    } finally {
        params.setUploading(false);
    }
}
